// Boomin' 加盟店管理画面 — shared design primitives.
// Built on the same design tokens as the consumer redesign (Pages/Redesign/shared)
// so the merchant dashboard matches the new Boomin' look: warm canvas, soft
// cards, the orange brand mark, and hand-drawn pictograms.

import React, { CSSProperties } from "react";
import { useHistory } from "react-router-dom";
import {
  BRAND,
  BRAND_SOFT,
  BG_CANVAS,
  TEXT,
  PictX,
  PictName,
  useBoominFx,
} from "../Redesign/shared";

export { BRAND, BRAND_SOFT, BG_CANVAS, TEXT, PictX };
export type { PictName };

export const BORDER = "#F0E6DC";
export const MUTED = "#8A7E74";
export const CARD_SHADOW = "0 12px 28px rgba(26,16,8,0.06)";

// Canonical coupon category set. Mirrors server/app/services/coupon.go
// normalizeCategory and the consumer redesign RedesignCategory enum.
export const COUPON_CATEGORIES: { value: string; label: string; icon: PictName }[] = [
  { value: "cafe", label: "カフェ", icon: "cafe" },
  { value: "meal", label: "ごはん", icon: "ramen" },
  { value: "sweet", label: "スイーツ", icon: "sweet" },
  { value: "bar", label: "バー", icon: "bar" },
  { value: "shop", label: "ショップ", icon: "shop" },
  { value: "salon", label: "サロン", icon: "salon" },
  { value: "activity", label: "体験", icon: "activity" },
];

export const categoryLabel = (value?: string): string =>
  COUPON_CATEGORIES.find((c) => c.value === value)?.label ?? "ショップ";

// ── Page shell ────────────────────────────────────────────────────────────
export const AdminPage: React.FC<{
  title: string;
  eyebrow?: string;
  icon?: PictName;
  action?: React.ReactNode;
  maxWidth?: number;
  children: React.ReactNode;
}> = ({ title, eyebrow, icon, action, maxWidth = 960, children }) => {
  useBoominFx();
  return (
    <div style={{ minHeight: "100%", background: BG_CANVAS, color: TEXT }}>
      <div
        style={{
          maxWidth,
          margin: "0 auto",
          padding: "32px 20px 80px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 28,
          }}
        >
          <div>
            {eyebrow && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  color: BRAND,
                  marginBottom: 8,
                }}
              >
                {eyebrow}
              </div>
            )}
            <h1
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                margin: 0,
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: "-0.01em",
              }}
            >
              {icon && (
                <span
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 13,
                    background: BRAND_SOFT,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PictX name={icon} size={24} stroke={BRAND} strokeWidth={1.7} />
                </span>
              )}
              {title}
            </h1>
          </div>
          {action && <div>{action}</div>}
        </div>
        {children}
      </div>
    </div>
  );
};

// ── Card ────────────────────────────────────────────────────────────────
export const Card: React.FC<{
  style?: CSSProperties;
  children: React.ReactNode;
}> = ({ style, children }) => (
  <div
    style={{
      background: "#fff",
      border: `1px solid ${BORDER}`,
      borderRadius: 20,
      boxShadow: CARD_SHADOW,
      ...style,
    }}
  >
    {children}
  </div>
);

// ── Buttons ───────────────────────────────────────────────────────────────
const baseBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  border: "1px solid transparent",
  borderRadius: 999,
  padding: "11px 22px",
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
  textDecoration: "none",
  lineHeight: 1,
  transition: "transform .12s ease, filter .12s ease",
};

export const PrimaryButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: PictName }
> = ({ icon, children, style, ...rest }) => (
  <button
    {...rest}
    style={{
      ...baseBtn,
      background: BRAND,
      color: "#fff",
      boxShadow: "0 8px 18px rgba(255,77,45,0.28)",
      ...style,
    }}
  >
    {icon && <PictX name={icon} size={18} stroke="#fff" strokeWidth={2} />}
    {children}
  </button>
);

export const GhostButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: PictName }
> = ({ icon, children, style, ...rest }) => (
  <button
    {...rest}
    style={{
      ...baseBtn,
      background: "#fff",
      color: TEXT,
      border: `1px solid ${BORDER}`,
      ...style,
    }}
  >
    {icon && <PictX name={icon} size={18} stroke={TEXT} strokeWidth={1.8} />}
    {children}
  </button>
);

// A small text link styled like the brand (used in tables for row actions).
export const LinkButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ children, style, ...rest }) => (
  <button
    {...rest}
    style={{
      background: "transparent",
      border: 0,
      padding: 0,
      cursor: "pointer",
      color: BRAND,
      fontSize: 13,
      fontWeight: 700,
      borderBottom: `1px solid ${BRAND}`,
      lineHeight: 1.4,
      ...style,
    }}
  >
    {children}
  </button>
);

// ── Form field wrapper ──────────────────────────────────────────────────
export const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{label}</label>
      {required && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: BRAND,
            background: BRAND_SOFT,
            borderRadius: 6,
            padding: "2px 6px",
          }}
        >
          必須
        </span>
      )}
    </div>
    {children}
    {hint && (
      <p style={{ margin: "6px 0 0", fontSize: 12, color: MUTED }}>{hint}</p>
    )}
  </div>
);

// Shared visual style for native inputs/selects inside admin forms.
export const fieldInputStyle: CSSProperties = {
  width: "100%",
  height: 46,
  padding: "0 14px",
  borderRadius: 12,
  border: `1px solid ${BORDER}`,
  background: "#fff",
  fontSize: 15,
  color: TEXT,
  outline: "none",
  boxSizing: "border-box",
};

// Category picker — pill toggles that register a hidden value with react-hook-form.
export const CategorySelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    {COUPON_CATEGORIES.map((c) => {
      const on = value === c.value;
      return (
        <button
          key={c.value}
          type="button"
          onClick={() => onChange(c.value)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "9px 15px",
            borderRadius: 999,
            border: `1px solid ${on ? BRAND : BORDER}`,
            background: on ? BRAND_SOFT : "#fff",
            color: on ? BRAND : TEXT,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            transition: "all .12s ease",
          }}
        >
          <PictX
            name={c.icon}
            size={17}
            stroke={on ? BRAND : MUTED}
            strokeWidth={1.7}
          />
          {c.label}
        </button>
      );
    })}
  </div>
);

// Brand-styled error banner used across the admin forms/pages.
export const AdminError: React.FC<{ error?: string }> = ({ error }) => {
  if (!error) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        background: "#FFF1EE",
        border: "1px solid #FFD3C8",
        borderRadius: 14,
        padding: "12px 16px",
        marginBottom: 20,
        color: "#B23A1E",
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        <PictX name="close" size={16} stroke="#B23A1E" strokeWidth={2} />
      </span>
      <span>{error}</span>
    </div>
  );
};

// Back link rendered above a form title.
export const BackLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => {
  const history = useHistory();
  return (
    <button
      type="button"
      onClick={() => history.push(to)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "transparent",
        border: 0,
        cursor: "pointer",
        color: MUTED,
        fontSize: 13,
        fontWeight: 700,
        padding: 0,
        marginBottom: 14,
      }}
    >
      <PictX name="chevronL" size={16} stroke={MUTED} strokeWidth={2} />
      {children}
    </button>
  );
};

// Empty-state block shown when a list has no rows yet.
export const EmptyState: React.FC<{
  icon: PictName;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <Card style={{ padding: "56px 24px", textAlign: "center" }}>
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 20,
        background: BRAND_SOFT,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
      }}
    >
      <PictX name={icon} size={32} stroke={BRAND} strokeWidth={1.6} />
    </div>
    <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6 }}>{title}</div>
    {description && (
      <div style={{ fontSize: 14, color: MUTED, marginBottom: action ? 20 : 0 }}>
        {description}
      </div>
    )}
    {action}
  </Card>
);
